import React, { FC } from 'react';
import Popover from '@mui/material/Popover';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { palette } from '../../theme';

interface COECreateTrainingPickerModalProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onSelect: (mode: 'log' | 'plan') => void;
}

interface OptionRowProps {
  icon: React.ReactNode;
  iconColor: string;
  title: string;
  description: string;
  onClick: () => void;
  divider?: boolean;
}

const OptionRow: FC<OptionRowProps> = ({ icon, iconColor, title, description, onClick, divider }) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      padding: '14px 16px',
      cursor: 'pointer',
      borderBottom: divider ? `1px solid ${palette.divider}` : 'none',
      transition: 'background-color 0.12s',
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
      },
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 44,
        height: 44,
        borderRadius: 2,
        backgroundColor: `${iconColor}18`,
        color: iconColor,
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography variant="subtitle2" fontWeight={700} lineHeight={1.3}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" lineHeight={1.4}>
        {description}
      </Typography>
    </Box>
  </Box>
);

const COECreateTrainingPickerModal: FC<COECreateTrainingPickerModalProps> = ({ anchorEl, onClose, onSelect }) => {
  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{
        sx: {
          borderRadius: 2,
          minWidth: 260,
          boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
          marginTop: '6px',
          overflow: 'hidden',
        },
      }}
    >
      <OptionRow
        icon={<FitnessCenterIcon fontSize="small" />}
        iconColor="#1976d2"
        title="Log New Training"
        description="Record a completed workout"
        onClick={() => onSelect('log')}
        divider
      />
      <OptionRow
        icon={<CalendarTodayIcon fontSize="small" />}
        iconColor="#2e7d32"
        title="Plan New Training"
        description="Schedule a future session"
        onClick={() => onSelect('plan')}
      />
    </Popover>
  );
};

export default COECreateTrainingPickerModal;
